import { Link, useParams } from 'react-router-dom';
import { catalogApi } from '../api/catalogApi';
import { progressApi } from '../api/progressApi';
import { useApi, useAsync } from '../hooks/useApi';
import { Loading, LoadError } from '../components/Loading';
import Crumbs from '../components/Crumbs';
import ProgressBar from '../components/ProgressBar';
import type { SectionProgress } from '../../../shared/types';

export default function ChapterPage() {
  const { chapterId = '' } = useParams();
  const api = useApi();

  const chapter = useAsync(() => catalogApi.chapter(chapterId, api), [chapterId, api]);
  const progress = useAsync(
    () => (chapter.data ? progressApi.book(chapter.data.bookId, api) : Promise.resolve(null)),
    [chapter.data?.bookId, api],
  );

  if (chapter.loading) {
    return (
      <main className="page">
        <Loading what="sections" />
      </main>
    );
  }
  if (chapter.error || !chapter.data) {
    return (
      <main className="page">
        <LoadError message={chapter.error ?? 'Chapter not found'} onRetry={chapter.reload} />
      </main>
    );
  }

  const data = chapter.data;
  const bySection = new Map<string, SectionProgress>(
    (progress.data?.sections ?? []).map((section) => [section.sectionId, section]),
  );

  return (
    <main className="page">
      <Crumbs
        items={[
          { label: 'Languages', to: '/' },
          { label: data.languageCode.toUpperCase(), to: `/languages/${data.languageCode}` },
          { label: data.bookTitle, to: `/books/${data.bookId}` },
          { label: `Chapter ${data.number}` },
        ]}
      />

      <header className="page-header">
        <p className="page-eyebrow">Chapter {data.number}</p>
        <h1>{data.title}</h1>
        <p className="page-description">{data.summary}</p>
      </header>

      <div className="section-head">
        <h2>Sections</h2>
        <span className="muted">
          {data.sectionCount} sections · {data.cardCount} cards
        </span>
      </div>

      <ul className="row-list">
        {data.sections.map((section) => {
          const state = bySection.get(section.id);
          const mastery = state && state.cardCount > 0 ? (state.cardsKnown / state.cardCount) * 100 : 0;
          return (
            <li key={section.id}>
              <Link to={`/sections/${section.id}`} className="row">
                <span className="row-number row-number--minor">{section.number}</span>
                <span className="row-body">
                  <span className="row-title">
                    {section.title}
                    {state?.studied && (
                      <span className="tag tag--read" title="You have read the study notes">
                        read
                      </span>
                    )}
                  </span>
                  <span className="row-summary">{section.summary}</span>
                  <span className="row-meta">
                    {section.studyMinutes} min read · {section.cardCount} cards
                    {state && state.cardsKnown > 0 && ` · ${state.cardsKnown} known`}
                  </span>
                  {state && state.cardsSeen > 0 && <ProgressBar value={mastery} compact />}
                </span>
                <span className="row-chevron" aria-hidden="true">
                  ›
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </main>
  );
}
